import type { ReactNode } from "react";
import {
    CounterpartyDropdown,
    type CounterpartyDropdownProps,
} from "@components/counterparty/CounterpartyDropdown";
import { FormikField } from "@components/forms/FormikField/FormikField";
import type { FieldPath, FieldPathEmptyValue } from "./fieldPath.types";
import { pickChromeProps } from "./kitChromeProps";
import { type KitClearableProps, toClearTarget } from "./kitClearable";
import type {
    KitFieldChromeProps,
    KitFieldControlProps,
} from "./kitFieldProps.types";

type CounterpartyFieldValue = string | string[] | null | undefined;

/** Paths of `Values` that hold one counterparty id. */
export type CounterpartyFieldPath<Values> = FieldPath<
    Values,
    string | null | undefined,
    string
>;

/** Paths of `Values` that hold counterparty ids, for `multiple`. */
export type CounterpartyFieldMultiplePath<Values> = FieldPath<
    Values,
    string[] | null | undefined,
    string[]
>;

type CounterpartyFieldBaseProps<Name extends string, Written> = Omit<
    KitFieldControlProps<Name, HTMLButtonElement, Written>,
    "required"
> &
    KitFieldChromeProps &
    Pick<CounterpartyDropdownProps, "buttonSize" | "container">;

export type CounterpartyFieldSingleProps<
    Values,
    Name extends string,
> = CounterpartyFieldBaseProps<
    Name,
    string | FieldPathEmptyValue<Values, Name>
> &
    KitClearableProps<FieldPathEmptyValue<Values, Name>> & {
        multiple?: false;
    };

export type CounterpartyFieldMultipleProps<Name extends string> =
    CounterpartyFieldBaseProps<Name, string[]> & { multiple: true };

export type CounterpartyFieldProps<Values, Name extends string> =
    | CounterpartyFieldSingleProps<Values, Name>
    | CounterpartyFieldMultipleProps<Name>;

/**
 * Counterparty picker bound to a typed Formik path: one counterparty id
 * without `multiple`, an id array with it. A single pick is cleared only when
 * `clearable` comes with an `emptyValue` the path allows; otherwise
 * unselecting keeps the last counterparty.
 */
export const CounterpartyField = <
    Values,
    Name extends
        | CounterpartyFieldPath<Values>
        | CounterpartyFieldMultiplePath<Values> =
        | CounterpartyFieldPath<Values>
        | CounterpartyFieldMultiplePath<Values>,
>(
    props: CounterpartyFieldProps<Values, Name>,
): ReactNode => {
    const { name, ref, disabled, buttonSize, container } = props;
    const controlOptions = {
        ref,
        disabled,
        buttonSize,
        container,
        "aria-labelledby": props["aria-labelledby"],
    };

    return (
        <FormikField<CounterpartyFieldValue>
            name={name}
            control="button"
            {...pickChromeProps(props)}
        >
            {({ field, helpers, invalid }) => {
                const onBlur = () => void helpers.setTouched(true);

                if (props.multiple === true) {
                    const { onValueChange } = props;
                    return (
                        <CounterpartyDropdown
                            {...controlOptions}
                            multiple
                            value={
                                Array.isArray(field.value) ? field.value : []
                            }
                            onValueChange={(counterpartyIds) => {
                                void helpers.setValue(counterpartyIds);
                                onValueChange?.(counterpartyIds);
                            }}
                            onBlur={onBlur}
                            invalid={invalid}
                        />
                    );
                }

                const { onValueChange } = props;
                const clearTarget = toClearTarget(props);
                return (
                    <CounterpartyDropdown
                        {...controlOptions}
                        // Forms that model "no counterparty" as "" show it as unselected.
                        value={
                            typeof field.value === "string" &&
                            field.value !== ""
                                ? field.value
                                : null
                        }
                        onValueChange={(counterpartyId) => {
                            if (counterpartyId !== null) {
                                void helpers.setValue(counterpartyId);
                                onValueChange?.(counterpartyId);
                            } else if (clearTarget) {
                                void helpers.setValue(clearTarget.emptyValue);
                                onValueChange?.(clearTarget.emptyValue);
                            }
                        }}
                        onBlur={onBlur}
                        invalid={invalid}
                    />
                );
            }}
        </FormikField>
    );
};
